// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import Video from 'models/propTypes/video';

import {
  showFullScreenPlayer,
  exitFullScreenPlayer,
  setHeroIndex,
} from 'actions/hero';

import { useRestoration } from 'hooks/restoration';
import { useAuthN } from 'hooks/auth/subscriptions';
import useTracking from 'hooks/analytics';

import CarouselSlide from 'components/pages/home/carousel/CarouselItem';
import CarouselDots from 'components/pages/home/carousel/CarouselDots';
import FocusGroup from 'components/focus/FocusGroup';
import device from 'common/support/device';
import appActions from 'common/support/applicationActions';
import focusManager from 'support/focus/FocusManager';
import { getCarouselMetadata } from 'support/analytics/analyticsHelper';
import styles from './Carousel.module.scss';

const TRANSITION_TIME = 1000;
const DEFAULT_INTERVAL = 4000;

const trackCarouselEvent = (trackEvent, { slideNum, action, title }) => {
  const eventData = {
    ...getCarouselMetadata(true, slideNum),
    moduleAction: action,
    moduleCampaign: 'hero carousel interactions',
    clickText: `hero carousel - ${title}`,
  };
  trackEvent('carousel interaction', eventData);
};

const Carousel = ({
  id,
  data,
  enableAutoScroll,
  carouselSlideClassname,
  onFocusIndexChanged,
  onFocusDelegated,
  onFocusSetOverride,
  restorationId,
  hideCarousel,
  shouldAnimate,
  onError,
  carouselFocused,
  onUpcomingEventClick,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { trackEvent } = useTracking();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [disableNextTransition, setDisableNextTransition] = useState(false);
  const [useCSSTransitions, setUseCssTransitions] = useState(true);
  const [rotationComplete, setRotationComplete] = useState(false);

  const auth = useSelector(state => state.auth);

  const fullScreenPlayer = useSelector(state => state.hero.fullScreenPlayer);
  const useConfigValue = useSelector(
    state => state.kitchenSink.data?.config?.modules?.timeouts?.enabled,
  );
  const ksRotationInterval = useSelector(
    state =>
      state.kitchenSink.data?.config?.modules?.timeouts?.params?.carouselSpeed,
  );
  const manualOverrideInterval = useSelector(
    state =>
      state.kitchenSink.data?.config?.modules?.timeouts?.params
        ?.carouselUserTimeout,
  );
  const setIndex = useSelector(state => state.hero.setIndex);

  useEffect(() => {
    if (setIndex !== null) {
      setCurrentIndex(setIndex);
      dispatch(setHeroIndex(null)); // reset back to null
    }
  }, [setIndex, dispatch]);

  const rotationInterval = useConfigValue
    ? ksRotationInterval * 1000
    : DEFAULT_INTERVAL;
  const autoScrollTime = TRANSITION_TIME + rotationInterval; // Incrementing `transitionTime` ensures auto scroll wont fire before transition finishes
  const [transitionCSSTransform, setTransitionCSSTransform] = useState(
    `transform ${TRANSITION_TIME}ms`,
  );
  const manualOverrideTime = useConfigValue
    ? manualOverrideInterval * 1000
    : DEFAULT_INTERVAL;

  /**
   * Set whether to use CSS transitions.
   */
  useEffect(() => {
    const { useCssTransitions } = device;

    if (typeof useCssTransitions !== 'undefined') {
      // Use to check whether to enable css transitions
      setUseCssTransitions(useCssTransitions);
      if (!useCssTransitions) setTransitionCSSTransform(''); // Remove style
    }
  }, [setUseCssTransitions]);

  const autoScrollIntervalRef = useRef();
  const timeoutRef = useRef();
  const prevIndex = useRef(currentIndex);

  const { setRestorationState, restorationState } = useRestoration(
    restorationId,
  );

  useEffect(() => {
    if (restorationState) {
      setCurrentIndex(restorationState.currentIndex);
      autoScrollIntervalRef.current = restorationState.intervalId;
      timeoutRef.current = restorationState.timeoutId;
    } else {
      setCurrentIndex(0);
    }
  }, [restorationState]);

  const onTileFocused = useCallback(
    focusIndex => {
      if (currentIndex !== focusIndex) {
        setTransitioning(useCSSTransitions);
        setCurrentIndex(focusIndex);
      }
    },
    [currentIndex, useCSSTransitions],
  );

  const focusTile = useCallback(
    index => {
      const focusedCtaId = `${id}-${index}`;
      focusManager.setCurrentFocus(focusedCtaId);
    },
    [id],
  );

  const scrollToNextTile = useCallback(
    (action, autoScroll = true, isFullScreenPlayer) => {
      let newIndex = currentIndex + 1;

      if (action === appActions.moveLeft) {
        newIndex = currentIndex - 1;
        if (newIndex < 0 && !isFullScreenPlayer) {
          focusManager.setCurrentFocus('left-nav-focusable-content');
          return;
        }
      } else if (newIndex >= data?.length) {
        newIndex = 0;
        setDisableNextTransition(true);
      }

      trackCarouselEvent(trackEvent, {
        slideNum: newIndex + 1,
        action: autoScroll ? 'impression-auto' : 'impression-swipe',
        title: data[newIndex]?.about?.title,
      });

      const isFocusedOnCarousel = focusManager.currentFocusId.includes(
        'home-carousel',
      );
      if (isFocusedOnCarousel) {
        focusTile(newIndex);
      }
      if (currentIndex + 1 >= data?.length && autoScroll) {
        setRotationComplete(true);
      }
    },
    [currentIndex, data, focusTile, trackEvent],
  );

  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = scrollToNextTile;
  });

  const isFirstItemPlayable = useAuthN(data[0]?.authTypes);

  const autoPlayVideo = useCallback(() => {
    const firstItemVideoId = data[0]?.id;
    if (isFirstItemPlayable && firstItemVideoId) {
      dispatch(showFullScreenPlayer(firstItemVideoId));
    } else {
      // play autoPlay object
      history.push({
        pathname: '/playback',
      });
    }
    trackCarouselEvent(trackEvent, {
      slideNum: 1,
      action: 'autoplay',
      title: data[0]?.about?.title,
    });
    setRotationComplete(false);
  }, [isFirstItemPlayable, dispatch, data, history, trackEvent]);

  const scrollToNext = useCallback((scrollAction = null) => {
    savedCallback.current(scrollAction);
  }, []);

  const addAutoScrollTimer = useCallback(() => {
    autoScrollIntervalRef.current = setInterval(scrollToNext, autoScrollTime);
  }, [autoScrollTime, scrollToNext]);

  const addManualTimer = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      scrollToNext();
      addAutoScrollTimer();
    }, manualOverrideTime);
  }, [addAutoScrollTimer, manualOverrideTime, scrollToNext]);

  const addIdleTimer = useCallback(() => {
    timeoutRef.current = setTimeout(autoPlayVideo, autoScrollTime);
  }, [autoPlayVideo, autoScrollTime]);

  const clearAutoScrollTimer = useCallback(() => {
    clearInterval(autoScrollIntervalRef.current);
    clearTimeout(timeoutRef.current);
  }, []);

  const firstTimeInFocus = useRef(true);
  useEffect(() => {
    if (data?.length && enableAutoScroll && carouselFocused) {
      if (!autoScrollIntervalRef.current && firstTimeInFocus.current) {
        addAutoScrollTimer();
        firstTimeInFocus.current = false;
      } else if (rotationComplete) {
        clearAutoScrollTimer();
        addIdleTimer();
      } else {
        clearAutoScrollTimer();
        addManualTimer();
      }
    }
    return () => {
      clearAutoScrollTimer();
    };
  }, [
    data,
    enableAutoScroll,
    autoScrollTime,
    carouselFocused,
    manualOverrideTime,
    rotationComplete,
    addAutoScrollTimer,
    scrollToNext,
    addManualTimer,
    clearAutoScrollTimer,
    addIdleTimer,
  ]);

  // Handle when carousel has rotated through all items
  useEffect(() => {
    if (rotationComplete) {
      clearAutoScrollTimer();
      addIdleTimer();
    }
  }, [
    rotationComplete,
    autoPlayVideo,
    autoScrollTime,
    clearAutoScrollTimer,
    addIdleTimer,
  ]);

  useEffect(() => {
    if (prevIndex !== currentIndex) {
      setRestorationState({
        currentIndex,
        prevIndex,
        intervalId: autoScrollIntervalRef.current,
        timeoutId: timeoutRef.current,
      });
    }
  }, [currentIndex, prevIndex, setRestorationState]);

  const [upcomingEvent, setUpcomingEvent] = useState(false);
  useEffect(() => {
    const eventStatus = data[currentIndex]?.event?.status;
    if (eventStatus === 'upcoming') {
      setUpcomingEvent(true);
    } else {
      setUpcomingEvent(false);
    }
  }, [currentIndex, data]);

  const onFocusSetOverrideFunction = useCallback(
    val => {
      onFocusSetOverride(val);
      return currentIndex;
    },
    [currentIndex, onFocusSetOverride],
  );

  const onFocusIndexOverride = () => {
    if (transitioning) {
      return 'handled';
    }

    return false;
  };

  const viewWidth = 1920;

  const showCarouselDots = !fullScreenPlayer && data?.length > 1;

  const handleError = error => {
    if (!fullScreenPlayer) return;
    onError(error);
  };

  const onAction = action => {
    if (hideCarousel || action === appActions.moveDown) {
      clearAutoScrollTimer();
      return;
    }

    if (action === appActions.moveLeft || action === appActions.moveRight) {
      const autoScroll = false;
      scrollToNextTile(action, autoScroll, fullScreenPlayer);
    }

    clearAutoScrollTimer();
    addManualTimer();
  };

  const navigateToPlayer = useCallback(
    (rowId, videoId) => {
      const params = new URLSearchParams();
      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      const search = params.toString();

      dispatch(exitFullScreenPlayer());

      history.push({
        pathname: '/playback',
        ...(search ? { search } : {}),
      });
    },
    [history, dispatch],
  );

  const navigateToHeroPlayer = useCallback(
    (rowId, videoId, video) => {
      const params = new URLSearchParams();
      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      const search = params.toString();

      if (
        auth.paramountPlus?.user?.data?.userPackage ===
          'CBS_ALL_ACCESS_LOW_COST_PACKAGE' &&
        (auth.adobe?.authN == null || !video?.authTypes.includes('adobe')) &&
        !video?.authenticationDetails?.paramountplus?.essentials &&
        video?.authenticationDetails?.paramountplus?.premium
      ) {
        // user auth is p+ essentials
        // user is either not logged in with mvpd or (if they are logged in with mvpd) the stream is not mvpd authorized
        // content is p+ premium only
        // show upgrade to watch screen
        history.push({
          pathname: '/upgradeToWatch',
          ...(search ? { search } : {}),
        });
      } else {
        dispatch(showFullScreenPlayer(videoId));

        history.push({
          pathname: '/hero',
          ...(search ? { search } : {}),
        });
      }
    },
    [history, dispatch, auth],
  );

  const handleOnClick = (videoId, isPlayable, video) => {
    clearAutoScrollTimer();

    if (upcomingEvent) {
      onUpcomingEventClick(videoId);
    } else {
      trackCarouselEvent(trackEvent, {
        slideNum: currentIndex + 1,
        action: 'click',
        title: data[currentIndex]?.about?.title,
      });

      if (isPlayable) {
        navigateToHeroPlayer(null, videoId, video);
      } else {
        navigateToPlayer(null, videoId);
      }
    }
  };

  const handlePostGame = videoId => {
    navigateToPlayer(null, videoId);
  };

  return (
    <div
      className={classNames(
        'overflow-hidden',
        'position-fixed',
        'w-100',
        fullScreenPlayer && 'h-100',
        styles.carousel,
        {
          [styles.animate]: !device.isSlowDevice && shouldAnimate,
          [styles.showCarousel]: !hideCarousel,
          [styles.hideCarousel]: hideCarousel,
        },
      )}
    >
      {!!data?.length && (
        <React.Fragment>
          <FocusGroup
            id={id}
            focusId={id}
            style={{
              transition: disableNextTransition
                ? 'transform 1ms'
                : transitionCSSTransform,
              // replace vw by px to fix transition animation not showing up on 2016 samsung tizen
              transform: `translateX(-${currentIndex * viewWidth}px)`,
              WebkitTransform: `translateX(-${currentIndex * viewWidth}px)`,
            }}
            className={classNames(
              styles.carouselViewport,
              'h-100',
              'w-100',
              'carousel-focus-group',
            )}
            onAction={onAction}
            onFocusIndexChanged={onFocusIndexChanged}
            onFocusDelegated={onFocusDelegated}
            onFocusSetOverride={onFocusSetOverrideFunction}
            onFocusIndexOverride={onFocusIndexOverride}
            onTransitionEnd={() => {
              setDisableNextTransition(false);
              setTransitioning(false);
            }}
            horizontal
          >
            {data?.map((video, index) => {
              const slideId = `${id}-${index}`;
              return (
                <CarouselSlide
                  className={classNames(
                    'carousel-slide',
                    carouselSlideClassname,
                  )}
                  key={slideId}
                  focusId={slideId}
                  onTileFocused={() => {
                    onTileFocused(index);
                  }}
                  hideCarousel={hideCarousel}
                  onError={handleError}
                  onClick={handleOnClick}
                  handlePostGame={handlePostGame}
                  video={video}
                  slideNum={index + 1}
                  totalNum={data?.length}
                />
              );
            })}
          </FocusGroup>
          {showCarouselDots && (
            <div
              className={classNames(
                'position-absolute d-flex justify-content-center',
                styles.carouselDots,
              )}
            >
              <CarouselDots
                currentIndex={currentIndex}
                numDots={data?.length}
              />
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

Carousel.propTypes = {
  data: PropTypes.arrayOf(Video),
  enableAutoScroll: PropTypes.bool,
  id: PropTypes.string.isRequired,
  onFocusIndexChanged: PropTypes.func,
  onFocusDelegated: PropTypes.func,
  onFocusSetOverride: PropTypes.func,
  carouselSlideClassname: PropTypes.string,
  restorationId: PropTypes.string,
  hideCarousel: PropTypes.bool,
  shouldAnimate: PropTypes.bool,
  onError: PropTypes.func,
  carouselFocused: PropTypes.bool,
  onUpcomingEventClick: PropTypes.func,
};

Carousel.defaultProps = {
  data: null,
  enableAutoScroll: false,
  onFocusIndexChanged: () => {},
  onFocusDelegated: () => {},
  onFocusSetOverride: () => {},
  carouselSlideClassname: '',
  restorationId: null,
  hideCarousel: false,
  shouldAnimate: true,
  onError: () => {},
  carouselFocused: false,
  onUpcomingEventClick: () => {},
};

export default Carousel;
